import {ChallengeResultTaskDto, ChallengeTaskDto} from "../api/model/user-response-dto";
import {powWorkerScript} from "./pow-worker-script";

export class WorkerPool {
  private objectUrl: string;
  private queue: {
    task: ChallengeTaskDto;
    difficulty: number;
    resolve: Function;
    reject: Function;
  }[] = [];
  private activeTasks = 0;
  private readonly maxWorkers: number;
  private workers: Worker[] = [];
  private isReset: boolean = false;

  constructor() {
    const blob = new Blob([powWorkerScript], { type: "application/javascript" });
    this.objectUrl = URL.createObjectURL(blob);
    this.maxWorkers = Math.min(Math.max(4, navigator.hardwareConcurrency || 4), 10);
  }

  public enqueue(task: ChallengeTaskDto, difficulty: number): Promise<ChallengeResultTaskDto> {
    return new Promise((resolve, reject) => {
      if (this.isReset) {
        reject(new Error("WorkerPool has been reset"));
        return;
      }
      this.queue.push({ task, difficulty, resolve, reject });
      this.processQueue();
    });
  }

  private processQueue() {
    if (this.queue.length === 0 || this.activeTasks >= this.maxWorkers || this.isReset) {
      return;
    }

    const { task, difficulty, resolve, reject } = this.queue.shift()!;
    this.activeTasks++;

    const worker = new Worker(this.objectUrl);
    this.workers.push(worker);
    worker.postMessage({ task, difficulty });

    worker.onmessage = (event) => {
      if (this.isReset) {
        reject(new Error("WorkerPool has been reset"));
      } else {
        resolve(event.data);
      }
      this.activeTasks--;
      this.workers.splice(this.workers.indexOf(worker), 1);
      worker.terminate();
      this.processQueue();
    };

    worker.onerror = () => {
      if (!this.isReset) {
        reject(new Error("Worker error"));
      } else {
        reject(new Error("WorkerPool has been reset"));
      }
      this.activeTasks--;
      this.workers.splice(this.workers.indexOf(worker), 1);
      worker.terminate();
      this.processQueue();
    };
  }

  public reset(): void {
    this.isReset = true;
    // Terminate all active workers
    for (const worker of this.workers) {
      worker.terminate();
    }
    this.workers = [];
    // Clear the queue
    this.queue = [];
  }
}
